import { AlertTriangle, AlertCircle, AlertOctagon, Info, ChevronRight } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const SEVERITY = {
  critical: { icon: AlertOctagon, tone: 'bg-rose-500/10 text-rose-700 dark:text-rose-300 border-rose-500/40', label: 'CRITICAL' },
  high:     { icon: AlertTriangle, tone: 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/40', label: 'HIGH' },
  medium:   { icon: AlertCircle,   tone: 'bg-primary/10 text-primary border-primary/30',                          label: 'MEDIUM' },
  advisory: { icon: Info,          tone: 'bg-slate-500/10 text-slate-600 dark:text-slate-300 border-slate-500/30', label: 'ADVISORY' },
};

export const ConflictPanel = ({ counts, findings, onJumpToRule, compact = false }) => {
  const ordered = ['critical', 'high', 'medium', 'advisory'];
  if (!findings) return null;
  const total = (counts?.critical || 0) + (counts?.high || 0) + (counts?.medium || 0) + (counts?.advisory || 0);
  return (
    <div data-testid="admin-conflict-panel" className="space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="text-xs font-semibold flex items-center gap-1.5">
          <AlertTriangle className="h-3.5 w-3.5 text-amber-500" /> Rule conflicts
        </div>
        <span className="text-[10px] text-muted-foreground">{total} finding{total === 1 ? '' : 's'}</span>
        <div className="flex-1" />
        {ordered.map((s) => {
          if (!counts?.[s]) return null;
          const { tone, label } = SEVERITY[s];
          return (
            <Badge key={s} variant="outline" className={cn('text-[10px] gap-1', tone)} data-testid={`admin-conflict-count-${s}`}>
              {label} · {counts[s]}
            </Badge>
          );
        })}
      </div>
      {total === 0 ? (
        <Card className="p-3 border-emerald-500/30 bg-emerald-500/5 text-emerald-700 dark:text-emerald-400 text-xs flex items-center gap-2" data-testid="admin-conflict-empty">
          <Info className="h-3.5 w-3.5" /> No conflicts detected. This rule set is clean.
        </Card>
      ) : (
        <div className="space-y-1">
          {findings.slice(0, compact ? 4 : 50).map((f, i) => {
            const { icon: Icon, tone, label } = SEVERITY[f.severity] || SEVERITY.advisory;
            return (
              <Card key={i} className={cn('p-2.5 border', tone)} data-testid={`admin-conflict-finding-${i}`}>
                <div className="flex items-start gap-2">
                  <Icon className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className={cn('text-[9px] uppercase tracking-wide', tone)}>{label}</Badge>
                      <span className="text-[10px] font-mono-num opacity-70">{f.code}</span>
                    </div>
                    <div className="mt-1 text-[12px] leading-relaxed">{f.message}</div>
                    {f.suggestion && (<div className="mt-0.5 text-[11px] italic opacity-75">{f.suggestion}</div>)}
                    {f.rule_ids?.length > 0 && onJumpToRule && (
                      <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                        {f.rule_ids.map((rid) => (
                          <Button key={rid} size="sm" variant="ghost" className="h-5 px-1.5 text-[10px] gap-0.5" onClick={() => onJumpToRule?.(rid)} data-testid={`admin-conflict-jump-${rid}`}>
                            {rid}<ChevronRight className="h-2.5 w-2.5" />
                          </Button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
          {findings.length > (compact ? 4 : 50) && (
            <div className="text-[10px] text-muted-foreground text-center pt-1">+ {findings.length - (compact ? 4 : 50)} more findings</div>
          )}
        </div>
      )}
    </div>
  );
};
